import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2 } from "lucide-react"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DatePopover } from "@/components/date-popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  CONTRACT_TYPE_VALUES,
  DEFAULT_FARMGATE_PRICE,
} from "@/features/contracts/components/contract-constants"
import { useCooperatives } from "@/features/cooperatives/hooks/use-cooperatives"
import type { OrderWriteRequest } from "@/features/orders/api/orders.api"
import { kgToMt, formatNumber } from "@/lib/format"
import type { Contract, OrderDetail } from "@/types/domain"

const orderFormSchema = z.object({
  groupId: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "number" ? v : Number(v)))
    .refine((n) => !Number.isNaN(n) && n > 0),
  agreedQuantity: z
    .union([z.string(), z.number()])
    .transform((v) => Number(v))
    .refine((n) => !Number.isNaN(n) && n > 0),
  contractType: z.string().min(1),
  orderNumber: z.string().min(1),
  deliveryAddress: z.string().min(1),
  qualityCriteria: z.string().nullable().optional().transform((v) => v ?? null),
  dateOfExportToPort: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v && v !== "" ? v : null)),
  contractDurationStart: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v && v !== "" ? v : null)),
  contractDurationEnd: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v && v !== "" ? v : null)),
  basePrice: z
    .union([z.string(), z.number()])
    .transform((v) => (v === "" ? null : Number(v)))
    .nullable(),
  agroforestryBonusPerKilo: z
    .union([z.string(), z.number()])
    .transform((v) => (v === "" ? null : Number(v)))
    .nullable(),
})

type OrderFormInput = z.input<typeof orderFormSchema>
type OrderFormOutput = z.output<typeof orderFormSchema>

interface OrderFormProps {
  contract: Contract
  order?: OrderDetail | null
  isSubmitting: boolean
  onSubmit: (payload: OrderWriteRequest) => void
}

export function OrderForm({ contract, order, isSubmitting, onSubmit }: OrderFormProps) {
  const { t } = useTranslation(["orders", "contracts", "common"])
  const navigate = useNavigate()
  const { data: cooperatives, isLoading: loadingCooperatives } = useCooperatives()

  const defaultPremium =
    (contract.localCertificationPremiumPerKilo ?? 0) +
    (contract.agroforestryBonusPerKilo ?? 0)

  const defaults = useMemo<OrderFormInput>(
    () => ({
      groupId: order?.groupId ? String(order.groupId) : ("" as unknown as number),
      agreedQuantity: (order ? kgToMt(order.agreedQuantity ?? 0) : "") as unknown as number,
      contractType: order?.contractType ?? contract.contractType ?? "SPOT_CONTRACTS",
      orderNumber: order?.orderNumber ?? "",
      deliveryAddress: order?.deliveryAddress ?? "",
      qualityCriteria: order?.qualityCriteria ?? "",
      dateOfExportToPort: order?.dateOfExportToPort
        ? String(order.dateOfExportToPort).slice(0, 10)
        : "",
      contractDurationStart: order?.contractDurationStart
        ? String(order.contractDurationStart).slice(0, 10)
        : "",
      contractDurationEnd: order?.contractDurationEnd
        ? String(order.contractDurationEnd).slice(0, 10)
        : "",
      basePrice: (order?.basePrice ?? DEFAULT_FARMGATE_PRICE) as unknown as number,
      agroforestryBonusPerKilo: (order?.agroforestryBonusPerKilo ??
        defaultPremium) as unknown as number,
    }),
    [contract.contractType, defaultPremium, order],
  )

  const form = useForm<OrderFormInput, unknown, OrderFormOutput>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: defaults,
  })

  useEffect(() => {
    form.reset(defaults)
  }, [defaults, form])

  const watchedQuantity = Number(form.watch("agreedQuantity") || 0)
  const watchedContractType = form.watch("contractType")

  const totalAllocatedExceptThis = (contract.Orders ?? [])
    .filter((o) => String(o.id) !== String(order?.id ?? ""))
    .reduce((sum, o) => sum + kgToMt(o.agreedQuantity ?? 0), 0)
  const remainingToAllocate = Math.max(
    (contract.quantity ?? 0) - totalAllocatedExceptThis - watchedQuantity,
    0,
  )

  function handleSubmit(values: OrderFormOutput) {
    const payload: OrderWriteRequest = {
      ...values,
      groupId: Number(values.groupId),
    }
    onSubmit(payload)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6"
        noValidate
      >
        <Card>
          <CardHeader>
            <CardTitle>{t("form.sections.main")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="orderNumber"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>{t("form.fields.orderNumber")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="groupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.cooperative")}</FormLabel>
                  <Select
                    value={field.value ? String(field.value) : ""}
                    onValueChange={field.onChange}
                    disabled={!!order?.id || loadingCooperatives}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("form.fields.selectCooperative")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(cooperatives ?? []).map((coop) => (
                        <SelectItem key={String(coop.id)} value={String(coop.id)}>
                          {coop.name ?? `#${coop.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agreedQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.agreedQuantity")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      min={0}
                      max={(contract.quantity ?? 0) - totalAllocatedExceptThis}
                      placeholder={t("form.fields.agreedQuantityHint", {
                        max: formatNumber(remainingToAllocate),
                      })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contractType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.contractType")}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("form.fields.selectOrderType")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRACT_TYPE_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>
                          {t(`options.contractTypes.${v}`, { ns: "contracts" })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedContractType === "SPOT_CONTRACTS" ? (
              <FormField
                control={form.control}
                name="dateOfExportToPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.fields.deadline")}</FormLabel>
                    <FormControl>
                      <DatePopover
                        value={field.value ? String(field.value).slice(0, 10) : ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <div className="grid grid-cols-2 gap-2 md:col-span-1">
                <FormField
                  control={form.control}
                  name="contractDurationStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("form.fields.deliveryPeriod")}</FormLabel>
                      <FormControl>
                        <DatePopover
                          value={field.value ? String(field.value).slice(0, 10) : ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contractDurationEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>&nbsp;</FormLabel>
                      <FormControl>
                        <DatePopover
                          value={field.value ? String(field.value).slice(0, 10) : ""}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="deliveryAddress"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>{t("form.fields.deliveryAddress")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="basePrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.basePrice")}</FormLabel>
                  <FormControl>
                    <Input type="number" step="any" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agroforestryBonusPerKilo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.fields.premium")}</FormLabel>
                  <FormControl>
                    <Input type="number" step="any" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="qualityCriteria"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>{t("form.fields.qualityCriteria")}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t("form.fields.qualityCriteriaPlaceholder")}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="bg-background sticky bottom-0 z-10 flex justify-end gap-2 border-t py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("actions.saving", { ns: "common" })}
              </>
            ) : order ? (
              t("editOrder")
            ) : (
              t("createOrder")
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
